/**
 * A program's home (25 Oct 2026, D-1025-PROGRAM; the 17 Oct review §3.4 template): the title, the program's own pages
 * as one row of links, ONE main button (Book for this program), then this program's "Needs your attention", then
 * "Coming up". Companion & Wellness also carries what used to sit on Volunteers — confirming a volunteer, the Director's
 * OK, outings with their Send, the VISIONS report on the month that is ready. Reading & Services keeps its own
 * close-outs and notes. The Housekeeping screens stay Housekeeping's alone; the older `/volunteers?request=|incident=|visions=`
 * addresses arrive here (Volunteers redirects them).
 */
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { RequestView } from '@shared/volunteers-api';
import { Button } from '@/components/ui/button';
import { Sheet } from '@/components/ui/sheet';
import { ConfirmSheet, CompanionTab } from '@/features/volunteers/CompanionTab';
import { VisionsCard } from '@/features/volunteers/Visions';
import { useBooking } from '@/features/booking/BookingProvider';
import { Icon } from '@/components/Icons';
import { minutesLabel } from '@shared/volunteers';
import { serviceDateKey } from '@shared/dates';
import { hasAnyRole, ACCESS } from '@shared/roles';
import type { Me } from '@/auth/useMe';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDayKey } from '@/lib/dates';
import { useInbox } from '@/lib/messages';
import { useCompanionTab } from '@/features/volunteers/api';
import { kindWords } from '@/features/volunteers/kindWords';
import { companionItemHref, itemsForProgram, itemWords } from '@/features/volunteers/companionItems';
import type { CompanionItem } from '@shared/volunteers-api';
import { NoVolunteerButtons } from '@/features/volunteers/NoVolunteerButtons';
import { ReadingCloseoutSheet, type ReadingVisitRef } from '@/features/volunteers/ReadingCloseoutSheet';
import { ReadingNoteReplySheet, type NoteRef } from '@/features/volunteers/ReadingNoteReplySheet';
import { postJson, useVolunteerRefresh } from '@/features/volunteers/api';

export function ProgramHome({ program, me }: { program: 'companion' | 'reading'; me: Me }) {
  const { t, i18n } = useTranslation();
  const booking = useBooking();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const staff = hasAnyRole(me.roles, ACCESS.staff);
  const tab = useCompanionTab(staff);
  // 23 Oct 2026 (D-1023-INBOX): replies and bounced emails wait in Messages → Inbox; this page says it once
  const inbox = useInbox(staff);
  const code = program === 'companion' ? 'COMPANION' : 'READING';
  const today = serviceDateKey();
  const visits = (tab.data?.requests ?? []).filter((r) => r.program === code && r.state !== 'cancelled' && r.dayKey >= today);
  // D-1019-ATTN: a new volunteer is under Volunteers. 25 Oct 2026: Reading's items under Reading only, Companion's under Companion only
  const items = itemsForProgram(tab.data?.items ?? [], tab.data?.requests ?? [], program);
  const readyMonth = (() => {
    const m = tab.data?.items.find((i) => i.kind === 'visionsReady')?.data.month;
    return typeof m === 'string' && /^\d{6}$/.test(m) ? m : null;
  })();
  const [closing, setClosing] = useState<ReadingVisitRef | null>(null);
  const [replying, setReplying] = useState<NoteRef | null>(null);
  const refresh = useVolunteerRefresh();
  // Eli, 17 Oct 2026: "I see it here. I can't interact with it" — every visit and every request item opens its sheet
  const [open, setOpen] = useState<RequestView | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const all = tab.data?.requests ?? [];
  // 18 Oct 2026: an item that is not a request (a new volunteer, a report, the forms…) opens its exact place too
  const placeWords = (i: CompanionItem) =>
    i.kind === 'followUp' ? t('compItem.openReport') : i.action === 'volunteer' || i.action === 'call' ? t('vol.join.openCard') : i.action === 'events' ? t('activities.openEvents') : i.action === 'review' || i.action === 'visions' ? t('vol.tab.review') : t('vol.open');
  const itemButton = (i: CompanionItem) => {
    const r = typeof i.data.requestId === 'string' ? all.find((x) => x.requestId === i.data.requestId) : undefined;
    if (i.kind === 'readingCloseout')
      return (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setClosing({ appointmentId: String(i.data.appointmentId), who: `${String(i.data.unit)} · ${String(i.data.first)}`, name: String(i.data.name).split(' ')[0] ?? '', day: formatDayKey(String(i.data.dayKey), i18n.language), duration: Number(i.data.duration ?? 60) })}
          data-testid={`reading-close-${String(i.data.appointmentId)}`}
        >
          {t('reading1023.closeOut')}
        </Button>
      );
    if (i.kind === 'readingNote')
      return (
        <>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setReplying({ appointmentId: String(i.data.appointmentId), name: String(i.data.name), note: String(i.data.note ?? '') })}
            data-testid={`reading-note-reply-${String(i.data.appointmentId)}`}
          >
            {t('reading1024.reply')}
          </Button>
          {Number(i.data.replied) > 0 ? (
            <span className="help" data-testid={`reading-note-replied-${String(i.data.appointmentId)}`}>
              {t('reading1024.replied', { count: Number(i.data.replied) })}
            </span>
          ) : null}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void postJson(`/api/volunteers/visits/${encodeURIComponent(String(i.data.appointmentId))}`, { what: 'note-seen' }).then(() => refresh())}
            data-testid={`reading-note-seen-${String(i.data.appointmentId)}`}
          >
            {t('reading1023.gotIt')}
          </Button>
        </>
      );
    if (r && i.kind === 'noVolunteer')
      return (
        <NoVolunteerButtons
          request={r}
          onOpen={(x, el) => {
            opener.current = el;
            setOpen(x);
          }}
        />
      );
    if (r) return openButton(r, `program-item-${i.id}`);
    return (
      <Link className="btn secondary sm" to={companionItemHref(i)} data-testid={`program-item-${i.id}`}>
        {placeWords(i)}
      </Link>
    );
  };
  const openButton = (r: RequestView | undefined, testId: string) =>
    r ? (
      <Button
        variant="secondary"
        size="sm"
        onClick={(e) => {
          opener.current = e.currentTarget;
          setOpen(r);
        }}
        data-testid={testId}
      >
        {t('vol.open')}
      </Button>
    ) : null;
  const requestParam = params.get('request');
  const incidentParam = params.get('incident');
  const visionsParam = params.get('visions');
  const clearParams = (...keys: string[]) => {
    const next = new URLSearchParams(params);
    for (const k of keys) next.delete(k);
    setParams(next, { replace: true });
  };
  // Reading's own request deep link opens its sheet once the data is here (Companion's opens inside CompanionTab)
  useEffect(() => {
    if (program !== 'reading' || !requestParam) return;
    const r = all.find((x) => x.requestId === requestParam);
    if (!r) return;
    setOpen(r);
    clearParams('request');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program, requestParam, all]);
  // a pages-row link (#requests, #outings, #visions, #coming-up) takes you there and says where you are
  useEffect(() => {
    const id = location.hash.slice(1);
    if (!id || !staff || tab.isLoading) return;
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ block: 'start' });
    el.querySelector<HTMLElement>('h2')?.setAttribute('tabindex', '-1');
    el.querySelector<HTMLElement>('h2')?.focus({ preventScroll: true });
  }, [location.hash, staff, tab.isLoading]);
  // a deep link to a request of the other program lands on that program's page
  const other = requestParam ? all.find((x) => x.requestId === requestParam) : undefined;
  if (other && (other.program === 'READING') !== (program === 'reading')) return <Navigate to={`${other.program === 'READING' ? '/reading' : '/companion'}?request=${encodeURIComponent(other.requestId)}`} replace />;

  const programName = program === 'companion' ? t('vol.program.companion') : t('vol.program.reading');
  const comingUp = (
    <Card as="section" id="coming-up" aria-labelledby="h-program-visits" data-testid="program-coming-up">
      <CardHeader>
        <CardTitle id="h-program-visits">{t('prog1025.comingUp')}</CardTitle>
      </CardHeader>
      <CardContent>
        {/* 1.5.13 §1c.2: Loading only while the first answer is on its way; a failed request says so; an empty week says so */}
        {tab.isLoading ? (
          <p className="help" role="status">
            {t('app.loading')}
          </p>
        ) : tab.isError && !tab.data ? (
          <p className="help" role="alert" data-testid="program-error">
            {t('vol.home.error')}
          </p>
        ) : !visits.length ? (
          <p className="help calm" data-testid="program-visits-none">
            {t('vol.home.nothingThisWeek')}
          </p>
        ) : null}
        <ul className="attn-list">
          {visits.map((r) => (
            <li key={r.requestId}>
              <span className="dot" style={{ background: r.state === 'confirmed' ? 'var(--st-conf-dot)' : 'var(--st-hold-dot)' }} />
              <div className="t">
                <b>
                  {formatDayKey(r.dayKey, i18n.language)} · {minutesLabel(r.minutes)} · {r.tenant.unit} · {r.tenant.firstName}
                </b>
                <span>
                  {kindWords(t, r)}
                  {r.code ? ` · ${r.code}` : ''} · {r.confirmed ? t('vol.home.with', { name: r.confirmed.name }) : t(`vol.req.state.${r.state}`, { name: r.claimedBy?.name ?? '', count: r.sentTo })}
                </span>
              </div>
              {openButton(r, `program-open-${r.requestId}`)}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
  const inboxLine = inbox.data?.count ? (
    <p className="inbox1023-program-line" data-testid="program-inbox-line">
      {t('inbox1023.line', { count: inbox.data.count })}{' '}
      <Link className="btn secondary sm" to="/messages?tab=inbox">
        {t('inbox1023.openIn')}
      </Link>
    </p>
  ) : null;
  // the program's own pages, as one row of links under the title (Companion: its four places; Reading: all its requests)
  const pages: { id: string; to: string; label: string }[] =
    program === 'companion'
      ? [
          { id: 'home', to: '/companion', label: t('prog1025.pages.home') },
          { id: 'requests', to: '/companion#requests', label: t('prog1025.pages.requests') },
          { id: 'outings', to: '/companion#outings', label: t('prog1025.pages.outings') },
          { id: 'visions', to: '/companion#visions', label: t('prog1025.pages.visions') },
        ]
      : [{ id: 'requests', to: '/reading#coming-up', label: t('prog1025.pages.allRequests') }];
  const volunteerLink = `/volunteers?service=${program}`;
  return (
    <section aria-labelledby="h-program" className="page" data-testid={`program-home-${program}`}>
      <div className="page-head">
        <div>
          <div className="eyebrow">{t('vol.home.eyebrow')}</div>
          <h1 id="h-program" tabIndex={-1}>
            {programName}
          </h1>
          {staff ? (
            <nav aria-label={t('prog1025.pagesLabel', { program: programName })} className="chips" data-testid="program-pages">
              {pages.map((pg) => (
                <Link key={pg.id} className="chip" to={pg.to} data-testid={`program-page-${pg.id}`}>
                  {pg.label}
                </Link>
              ))}
              <Link className="chip" to={volunteerLink} data-testid="program-page-volunteers">
                {t('prog1025.pages.volunteers')}
              </Link>
            </nav>
          ) : null}
        </div>
        {staff && booking.canBook ? (
          <div className="chips">
            <Button onClick={(e) => booking.openBook({ program, lockProgram: true }, e.currentTarget)} data-testid={`program-book-${program}`}>
              <Icon name="plus" /> {program === 'companion' ? t('prog1025.book.companion') : t('prog1025.book.reading')}
            </Button>
          </div>
        ) : null}
      </div>
      {!staff ? (
        <Card>
          <CardContent>
            <p>{t('vol.home.staffOnly')}</p>
          </CardContent>
        </Card>
      ) : program === 'companion' ? (
        <>
          <CompanionTab
            program="companion"
            data={tab.data}
            loading={tab.isLoading}
            failed={tab.isError && !tab.data}
            todayKey={today}
            openRequest={requestParam}
            openIncident={incidentParam}
            onOpened={() => clearParams('request', 'incident')}
            after={
              <>
                {inboxLine}
                <div style={{ marginTop: 18 }}>{comingUp}</div>
              </>
            }
          />
          <VisionsCard todayKey={today} openMonth={/^\d{6}$/.test(visionsParam ?? '') ? visionsParam : null} readyMonth={readyMonth} onOpened={() => clearParams('visions')} />
        </>
      ) : (
        <>
          <Card as="section" aria-labelledby="h-program-attn" data-testid={`program-attention-${program}`}>
            <CardHeader>
              <CardTitle id="h-program-attn">{t('attention.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              {items.length ? (
                <ul className="attn-list attn-guard">
                  {items.map((i) => (
                    <li key={i.id}>
                      <span className="dot" style={{ background: 'var(--st-attn-dot)' }} />
                      <div className="t">
                        <b>{itemWords(i, t, i18n.language).text}</b>
                        <span>{itemWords(i, t, i18n.language).sub}</span>
                      </div>
                      {itemButton(i)}
                    </li>
                  ))}
                </ul>
              ) : tab.isLoading ? (
                <p className="help">{t('app.loading')}</p>
              ) : tab.isError && !tab.data ? (
                <p className="help">{t('vol.home.error')}</p>
              ) : (
                <p className="help calm">{t('vol.tab.none')}</p>
              )}
              {inboxLine}
            </CardContent>
          </Card>
          <div style={{ marginTop: 18 }}>{comingUp}</div>
        </>
      )}
      <Sheet open={!!closing} onOpenChange={(o) => (!o ? setClosing(null) : undefined)}>
        {closing ? <ReadingCloseoutSheet visit={closing} onClose={() => setClosing(null)} /> : null}
      </Sheet>
      <Sheet open={!!replying} onOpenChange={(o) => (!o ? setReplying(null) : undefined)}>
        {replying ? <ReadingNoteReplySheet note={replying} onClose={() => setReplying(null)} /> : null}
      </Sheet>
      <Sheet open={!!open} onOpenChange={(o) => (!o ? setOpen(null) : undefined)}>
        {open ? <ConfirmSheet r={open} onClose={() => setOpen(null)} returnFocusTo={opener} todayKey={today} /> : null}
      </Sheet>
    </section>
  );
}
